import { Box, Button, Container, IconButton, Stack, Typography } from "@mui/material";
import axios from "axios";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import { MaterialReactTable, MRT_ColumnFiltersState } from "material-react-table";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import Iconify from "../../../minimals/components/iconify";
import { UserContext } from "../../contexts/UserContext";
import { InternalUser } from "../../types/app";
import { getColumns } from "./columns";
import FundingAuthorizationActions from "../FundingAuthorizationActions";
import { FundingAuthorizationContext } from "../FundingAuthorizationContext";
import NewFundingPackageModal from "../NewFundingPackageModal";
import { FundingPackage, FundingPackageStatus } from "../types";
import { useParams } from "react-router-dom";

export default function FundingAuthorization() {
  const { user } = useContext(UserContext);
  const isFunder = !!user?.roles.some((r) => r.internalUserRoleType?.name === "Funder");

  const { dateRange, setDateRange, assignedToMe, loading, setAssignedToMe, tableRef, assignedToUser, fundingPackages, fedReferences } =
    useContext(FundingAuthorizationContext);
  const queryParams = new URLSearchParams(location.search);
  const { orderNumber } = useParams<{ orderNumber?: string }>();
  const assignedToId = queryParams.get("assignedtoid");
  const isUnassignedOnly = Number(assignedToId) == -1;
  const filtersInitialized = useRef(!!user?.email);
  const isReady = !loading && !!user && filtersInitialized.current;
  const [enableOrdering, setEnableOrdering] = useState(false);
  const [tableData, setTableData] = useState<any[]>([fundingPackages]);
  const [openNewPackageModal, setOpenNewPackageModal] = useState(false);
  const [search, setSearch] = useState("");
  const getDefaultFilters = (): MRT_ColumnFiltersState => {
    const filters: MRT_ColumnFiltersState = [];
    if (user?.email && isFunder) {
      filters.push(
        { id: "createdByEmail", value: user.email },
        {
          id: "status",
          value: [
            String(FundingPackageStatus.Draft),
            String(FundingPackageStatus.Requested),
            String(FundingPackageStatus.Deficient)
          ]
        }
      );
    }
    return filters;
  };
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(getDefaultFilters());
  const [filtersModified, setFiltersModified] = useState(false);
  const defaultFiltersRef = useRef(getDefaultFilters());

  const handleColumnFiltersChange = useCallback(
    (updaterOrValue: MRT_ColumnFiltersState | ((prev: MRT_ColumnFiltersState) => MRT_ColumnFiltersState)) => {
      setColumnFilters((prev) => {
        const next = typeof updaterOrValue === "function" ? updaterOrValue(prev) : updaterOrValue;
        const defaultFilters = defaultFiltersRef.current;
        const isDefault =
          next.length === defaultFilters.length &&
          defaultFilters.every((df) => next.some((nf) => nf.id === df.id && JSON.stringify(nf.value) === JSON.stringify(df.value)));
        setFiltersModified(!isDefault);
        return next;
      });
    },
    []
  );

  const preFilteredPackages = useMemo(() => {
    if (orderNumber) {
      return fundingPackages.filter((t) => t.orderNumber.includes(search));
    }
    if (filtersModified) return fundingPackages;

    const threeDaysAgo = dayjs().subtract(3, "days");
    const today = dayjs().startOf("day");
    return fundingPackages.filter((pkg) => {
      if (pkg.status === FundingPackageStatus.Funded) {
        return !!pkg.fundedDate && dayjs(pkg.fundedDate).isAfter(threeDaysAgo);
      }
      if (pkg.status === FundingPackageStatus.Cancelled) {
        return !!pkg.cancelledDate && dayjs(pkg.cancelledDate).isAfter(today);
      }
      return true;
    });
  }, [fundingPackages, filtersModified, orderNumber, search]);
  const noResultsFromParam = orderNumber && search && preFilteredPackages.length === 0;

  useEffect(() => {
    setTableData(tableData);
  }, [tableData]);

  useEffect(() => {
    if (orderNumber) {
      setSearch(orderNumber);
    }
  }, [orderNumber]);

  useEffect(() => {
    if (user?.email && !filtersInitialized.current) {
      filtersInitialized.current = true;
      setColumnFilters(getDefaultFilters());
    }
  }, [user?.email]);

  const onRowsDeselected = () => {
    if (tableRef.current) {
      tableRef.current.toggleAllRowsSelected(false);
    }
  };

  const columns = getColumns(fedReferences);

  return (
    <>
      <Helmet>
        <title> Funding Packages | TitleQ</title>
      </Helmet>
      <Container maxWidth="xl">
        <Typography variant="h3">My Funding Packages</Typography>
        <Typography variant="body2" color="text.secondary">
          Create and manage wire requests.
        </Typography>
        <Stack direction="row" justifyContent="space-between" mt={4}>
          <Typography variant="h4" marginBottom={2}>
            Package History
            {isUnassignedOnly && " - Unassigned"}
            {assignedToUser && ` - Assigned to ${assignedToUser.firstName} ${assignedToUser.lastName}`}
          </Typography>
          <Button
            color="primary"
            variant="contained"
            startIcon={<Iconify icon="eva:plus-fill" />}
            onClick={() => setOpenNewPackageModal(true)}
            size="small"
          >
            New Funding Package
          </Button>
        </Stack>
        {!isReady ? null : noResultsFromParam ? (
          <Box textAlign="center" py={4}>
            <Typography variant="h6" gutterBottom>
              No results found
            </Typography>

            <Button variant="contained" onClick={() => setOpenNewPackageModal(true)}>
              Create new package for order #{orderNumber}
            </Button>
          </Box>
        ) : (
          <MaterialReactTable
            enableStickyHeader
            columns={columns}
            data={preFilteredPackages}
            enableGlobalFilter
            enableFacetedValues
            onColumnFiltersChange={handleColumnFiltersChange}
            enableColumnOrdering={enableOrdering}
            enableRowSelection
            autoResetPageIndex={false}
            // enableRowActions={true}
            globalFilterFn="includesString"
            initialState={{
              density: "compact",
              pagination: { pageIndex: 0, pageSize: 30 },
              sorting: isFunder ? [{ id: "submitted", desc: true }] : [],
              columnVisibility: {
                createdByEmail: isFunder ? true : false
              },
              showColumnFilters: isFunder ? true : false
            }}
            // Leaves room for the page header, toolbars and main's bottom padding so only the table scrolls
            muiTableContainerProps={{ sx: { maxHeight: { xs: "calc(100vh - 448px)", lg: "calc(100vh - 372px)" } } }}
            muiTablePaperProps={{ sx: { border: (theme) => `1px solid ${theme.palette.grey[400]}` } }}
            muiTableBodyProps={{
              sx: {
                "& .MuiTableRow-root:hover .MuiTableCell-root": {
                  backgroundColor: "inherit !important"
                }
              }
            }}
            muiTableBodyRowProps={({ row }) => {
              return {
                style: row.index % 2 === 0 ? { backgroundColor: "#f4f4f4" } : {}
              };
            }}
            state={{
              showSkeletons: loading,
              columnFilters
            }}
            renderTopToolbarCustomActions={({ table }) => {
              return (
                <Box sx={{ flexGrow: 1, position: "relative" }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <FundingAuthorizationActions
                      dateRange={dateRange}
                      setDateRange={setDateRange}
                      assignedToMe={assignedToMe}
                      setAssignedToMe={setAssignedToMe}
                      table={table}
                      hideAssignedToMeOptions={!!assignedToId}
                      onRowsDeselected={onRowsDeselected}
                    />
                  </Stack>
                </Box>
              );
            }}
          />
        )}
      </Container>

      {openNewPackageModal && (
        <NewFundingPackageModal
          isOpen={openNewPackageModal}
          setIsOpen={setOpenNewPackageModal}
          onCloseNewFundingPackageModal={() => setOpenNewPackageModal(false)}
          orderNumberParam={orderNumber}
        />
      )}
    </>
  );
}
